import React, { Key, useEffect, useMemo, useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import {
  ButtonCommon,
  ColumnSelector,
  ExcelOperationsHandler,
  SearchByFields,
  showAlertNotification,
  TopHeader,
} from 'components/common';
import { Columns, ListItemKPI, ProductColumn, QueryParams, Status } from 'types';
import { Kanban, Plus } from '@phosphor-icons/react';
import { useGetAllBrandsQuery, useGetAllCategoriesQuery, useGetProductsQuery, useGetStatusQuery } from 'services';
import { Product } from 'models';
import { useNavigate } from 'react-router-dom';
import { ProductTable } from 'components/Product';
import { useCreateImportproductsMutation, useLazyGetExportProductsQuery } from 'services/importProduct.service';
import { createFile } from 'utils/filePath';
import { getFilePath } from 'utils';
const ProductListPage: React.FC = () => {
  const navigate = useNavigate();
  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>();
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({});
  const [triggerExport] = useLazyGetExportProductsQuery();
  const [createImportProduct] = useCreateImportproductsMutation();
  const [tableFilterSetter, setTableFilterSetter] = useState<React.Dispatch<React.SetStateAction<QueryParams>> | null>(
    null,
  );
  const { data, isError, refetch } = useGetProductsQuery({
    query: {
      size: 10,
      page: 1,
      include: 'product|category|status|brand|baseUnit|currencyUnit',
      filter: 'isActive:eq:true',
      sort: 'updatedAt:desc',
    } as QueryParams,
  });
  const [kips, setKips] = useState<{
    prActive: number;
    prInactive: number;
    prDraft: number;
  }>({
    prActive: 0,
    prInactive: 0,
    prDraft: 0,
  });
  const { data: status, isLoading } = useGetStatusQuery({
    query: {
      filter: 'type:eq:product',
    },
  });
  const { data: categories } = useGetAllCategoriesQuery({
    query: { size: 'unlimited', include: 'parent' },
  });
  const { data: brands } = useGetAllBrandsQuery({
    query: { size: 'unlimited' },
  });

  const query = {
    fileType: 'xlsx',
    size: 'unlimited',
    include: 'product.productVersions|category|brand|status|productUnits.unit|currencyUnit|baseUnit',
    sort: 'updatedAt:desc',
  };
  const sampleCsvUrl = `${getFilePath()}/general/csv-template/raw-product-data.csv`;
  const defaultUploadSubtitle = `Tải tệp Excel mẫu tại đây: <a href="${sampleCsvUrl}" target="_blank" rel="noopener noreferrer"><span class="text-blue-500 font-semibold">danh_sach_san_pham.csv</span></a>`;
  const columns: ColumnsType<ProductColumn> = useMemo(() => {
    // if (!status?.data || !categories?.data?.rows) return [];
    const arrayColumns = [
      {
        title: 'MÃ SẢN PHẨM',
        dataIndex: 'product.code',
        key: 'code',
        sorter: (a: ProductColumn, b: ProductColumn) => a['product.code'].localeCompare(b['product.code']),
        showSorterTooltip: false,
        filterSearch: true,
        onFilter: (value: boolean | Key, record: ProductColumn) => {
          return record.code === value;
        },
      },
      {
        title: 'TÊN SẢN PHẨM',
        dataIndex: 'fullName',
        key: 'fullName',
        sorter: (a: ProductColumn, b: ProductColumn) => a.name.localeCompare(b.name),
        showSorterTooltip: false,
        filterSearch: true,
      },
      {
        title: 'VERSION',
        dataIndex: 'version',
        key: 'version',
        showSorterTooltip: false,
        sorter: (a: ProductColumn, b: ProductColumn) => Number(a.version || 0) - Number(b.version || 0),
        filterSearch: true,
        disable: true,
      },
      {
        title: 'DANH MỤC',
        dataIndex: 'category.name',
        key: 'category',
        filters: categories?.data?.rows?.map((item) => ({
          text: item.name,
          value: item.name,
        })),
        filterSearch: true,
        sorter: (a: ProductColumn, b: ProductColumn) => a['category.name'].localeCompare(b['category.name']),
        onFilter: (value: boolean | Key, record: ProductColumn) => {
          return record.categoryId === value;
        },
        showSorterTooltip: false,
      },
      {
        title: 'THƯƠNG HIỆU',
        dataIndex: 'brand.name',
        key: 'brand',
        filters: brands?.data?.rows?.map((item) => ({
          text: item.name,
          value: item.name,
        })),
        filterSearch: true,
        sorter: (a: ProductColumn, b: ProductColumn) => a['brand.name']?.localeCompare(b['brand.name'] || ''),
        onFilter: (value: boolean | Key, record: ProductColumn) => {
          return record.brandId === value;
        },
        showSorterTooltip: false,
      },
      {
        title: 'TỒN KHO',
        dataIndex: 'product.quantity',
        key: 'quantity',
        sorter: (a: ProductColumn, b: ProductColumn) => a.stock - b.stock,
        showSorterTooltip: false,
        filterSearch: true,
      },
      {
        title: 'TRẠNG THÁI',
        dataIndex: 'status.id',
        key: 'status',
        filters:
          (status?.data as unknown as { id: number; name: string }[])?.map((item) => ({
            text: Status[item.name?.toLocaleString()?.toUpperCase() as keyof typeof Status],
            value: item.id,
          })) || [],
        onFilter: (value: boolean | Key, record: ProductColumn) => record.status === value,
        showSorterTooltip: false,
        sorter: (a: ProductColumn, b: ProductColumn) => a['status.name'].localeCompare(b['status.name']),
        filterSearch: true,
      },
      {
        title: 'GIÁ BÁN',
        dataIndex: 'salePrice',
        key: 'salePrice',
        showSorterTooltip: false,
        sorter: (a: ProductColumn, b: ProductColumn) => Number(a.salePrice || 0) - Number(b.salePrice || 0),
        filterSearch: true,
      },
      {
        title: 'GIÁ NHẬP',
        dataIndex: 'costPrice',
        key: 'costPrice',
        showSorterTooltip: false,
        sorter: (a: ProductColumn, b: ProductColumn) => Number(a.costPrice || 0) - Number(b.costPrice || 0),
        filterSearch: true,
      },
      {
        title: 'ĐƠN VỊ',
        dataIndex: 'baseUnit.name',
        key: 'baseUnit.name',
        showSorterTooltip: false,
        sorter: (a: ProductColumn, b: ProductColumn) =>
          (a?.['baseUnit.name'] || '')?.localeCompare(b['baseUnit.name'] || ''),
        filterSearch: true,
      },
      {
        title: 'SKU',
        dataIndex: 'product.barcode',
        key: 'product.barcode',
        showSorterTooltip: false,
        sorter: (a: ProductColumn, b: ProductColumn) => a['product.barcode'].localeCompare(b['product.barcode']),
        filterSearch: false,
      },
    ];
    setCheckedList(arrayColumns.map((column) => column.key));
    return arrayColumns;
  }, [status?.data, isLoading, categories?.data?.rows]);
  const getFilteredQuerySetter = (setter: React.Dispatch<React.SetStateAction<QueryParams>>) => {
    setTableFilterSetter(() => setter);
  };
  const handleSearch = (query: QueryParams) => {
    if (tableFilterSetter) {
      try {
        // Apply the ProductTable's filter directly
        tableFilterSetter({
          ...query,
          searchKey: 'name|color|dimension|sizeSpec|product.code',
          include: 'product.productVersions|category|brand|status|productUnits.unit|currencyUnit|baseUnit',
        } as unknown as QueryParams);
      } catch (error) {
        console.error('Error handling search:', error);
        setFilteredQuery(query);
      }
    } else {
      // Fallback to local state if table setter is not available
      setFilteredQuery(query);
    }
  };
  const initData = () => {
    try {
      const mapKips = (
        data?.data as {
          rows: Product[];
          count: number;
          kpis: {
            prActive: number;
            prInactive: number;
            prDraft: number;
          };
        }
      ).kpis;
      setKips(mapKips);
    } catch (error) {
      console.log('initData: ', error);
    }
  };
  const exportHandlers = {
    onExportTemplate: async () => {
      try {
        const templateUrl = `${getFilePath()}/general/csv-template/raw-product-data.csv`;

        createFile(templateUrl);

        showAlertNotification({
          type: 'success',
          title: 'Tải template',
          message: 'File template đã được tải xuống thành công.',
        });
      } catch {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi tải template',
          message: 'Không thể tải file template. Vui lòng thử lại.',
        });
      }
    },

    onExport: async () => {
      try {
        const { data } = await triggerExport({
          query: {
            ...query,
          },
        });

        if (data?.fileUrl) {
          createFile(data.fileUrl);
          showAlertNotification({
            type: 'success',
            title: 'Xuất dữ liệu',
            message: 'Đã xuất toàn bộ sản phẩm thành công.',
          });
        }
      } catch {
        showAlertNotification({
          type: 'error',
          title: 'Xuất dữ liệu',
          message: 'Có lỗi xảy ra khi xuất toàn bộ sản phẩm.',
        });
      }
    },

    // onExportByView: async () => {
    //   try {
    //     const { data } = await triggerExport({
    //       query: { fileType: 'xlsx', size: 'unlimited', readOnly: 'true' },
    //     });

    //     if (data?.fileUrl) {
    //       const link = document.createElement('a');
    //       link.href = data.fileUrl;
    //       link.setAttribute('download', 'products-view.xlsx');
    //       document.body.appendChild(link);
    //       link.click();
    //       link.remove();

    //       showAlertNotification({
    //         type: 'success',
    //         title: 'Xuất dữ liệu theo view',
    //         message: 'Đã xuất danh sách sản phẩm theo view thành công.',
    //       });
    //     }
    //   } catch {
    //     showAlertNotification({
    //       type: 'error',
    //       title: 'Xuất dữ liệu theo view',
    //       message: 'Có lỗi xảy ra khi xuất dữ liệu theo view.',
    //     });
    //   }
    // },

    onExportByFilter: async () => {
      try {
        console.log(filteredQuery);
        const { data } = await triggerExport({
          query: {
            ...filteredQuery,
            ...(filteredQuery?.subQuery !== undefined && { subQuery: filteredQuery.subQuery ? 'true' : 'false' }),
            ...query,
            checkedList: checkedList?.join(','),
          } as QueryParams,
        });

        if (data?.fileUrl) {
          createFile(data.fileUrl);

          showAlertNotification({
            type: 'success',
            title: 'Xuất dữ liệu theo bộ lọc',
            message: 'Đã xuất sản phẩm theo bộ lọc thành công.',
          });
        }
      } catch {
        showAlertNotification({
          type: 'error',
          title: 'Xuất dữ liệu theo bộ lọc',
          message: 'Có lỗi xảy ra khi xuất dữ liệu theo bộ lọc.',
        });
      }
    },

    onUploadComplete: async (files: File[], key?: string[]) => {
      const file = files?.[0];
      const ext = file?.name.split('.').pop()?.toLowerCase();

      let fileType: string = 'CSV';
      if (ext === 'xlsx' || ext === 'xls') {
        fileType = 'EXCEL';
      }

      try {
        await createImportProduct({
          filePath: key?.[0] as string,
          fileType,
        }).unwrap();

        showAlertNotification({
          type: 'success',
          title: 'Nhập dữ liệu',
          message: 'File Excel đã được nhập thành công.',
        });
      } catch {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi nhập dữ liệu',
          message: 'Đã xảy ra lỗi khi xử lý file Excel. Vui lòng kiểm tra định dạng file và thử lại.',
        });
      }
    },
  };
  useEffect(() => {
    if (data && !isError) {
      initData();
    } else if (isError) {
      refetch();
    }
  }, [data]);
  // Sample data with proper typing
  const columnsCheck: Columns[] = columns.map((column) => {
    return {
      key: column.key as string,
      title: column.title as string,
    };
  });
  const listItemTopHeader: ListItemKPI[] = [
    {
      icon: <Kanban size={32} />,
      value: (kips.prActive + kips.prInactive + kips.prDraft).toString() || '0',
      description: 'Tổng số lượng ',
    },
    {
      icon: <Kanban size={32} />,
      value: kips.prActive.toString() || '0',
      description: `Đang hoạt động`,
      iconDescription: (
        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
          <circle cx='5' cy='5' r='5' fill='#31A168' />
        </svg>
      ),
    },
    {
      icon: <Kanban size={32} />,
      value: kips.prInactive.toString() || '0',
      description: 'Ngưng hoạt động',
      iconDescription: (
        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
          <circle cx='5' cy='5' r='5' fill='#FF5454' />
        </svg>
      ),
    },
    {
      icon: <Kanban size={32} />,
      value: kips.prDraft.toString() || '0',
      description: 'Bản nháp',
      iconDescription: (
        <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
          <circle cx='5' cy='5' r='5' fill='black' fillOpacity='0.56' />
        </svg>
      ),
    },
  ];
  const handleCreateProduct = (): void => {
    try {
      navigate('/items/products/create-product');
    } catch (error) {
      console.log('handleCreateProduct: ', error);
    }
  };

  return (
    <>
      <TopHeader title='Danh sách sản phẩm' subTitle='Danh sách sản phẩm' listItem={listItemTopHeader} />
      <div className='pt-[16px] bg-[var(--bgGrey)]'>
        <div className='w-[100%] bg-[var(--textWhite)] px-[24px] py-[16px]'>
          <div className='flex lg:flex-row flex-col justify-between items-center'>
            <h1 className='text-[18px] leading-[26px] font-[700]'>DANH SÁCH SẢN PHẨM</h1>
            <div className='flex gap-2'>
              <div className='flex-1 max-w-[250px]'>
                <SearchByFields
                  filteredQuery={filteredQuery}
                  setFilteredQuery={handleSearch}
                  searchFields={['name', 'product.code', 'product.barcode']}
                  placeholder='Tìm kiếm theo tên hoặc mã...'
                />
              </div>
              <ColumnSelector allColumns={columnsCheck} selectedColumns={checkedList} onChange={setCheckedList} />
              <ExcelOperationsHandler
                entityType='attribute'
                entityName='thuộc tính'
                onRefresh={refetch}
                menuPosition='right'
                uploadSubtitle={defaultUploadSubtitle}
                filterParams={filteredQuery}
                customHandlers={exportHandlers}
              />
              <ButtonCommon
                icon={<Plus size={20} />}
                color='primary'
                onClick={handleCreateProduct}
                size='xs'
                radius='rounded'
              >
                Thêm sản phẩm mới
              </ButtonCommon>
            </div>
          </div>
        </div>
        {columns.length > 0 && checkedList && checkedList?.length > 0 && (
          <ProductTable
            newColumns={columns.map((item) => ({
              ...item,
              hidden: !checkedList?.includes(item.key as string),
            }))}
            draftId={status?.data.filter((item) => item.code === 'pr-draft')[0]?.id || ''}
            onParentFilter={setFilteredQuery}
            onGetFilteredQuerySetter={getFilteredQuerySetter}
          />
        )}
      </div>
    </>
  );
};

export default ProductListPage;
